import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import type { BalanceType } from '@vista-digital/ocapi-types/v1'
import { getBalanceTypeById, getMemberBalances, logger } from '@vista-digital/occ-domain'
import type { MemberBalanceProps, OccI18N, OccMemberDisplayText } from '@vista-digital/occ-ui'
import {
    DisplayText,
    MemberBalancePrimaryValue,
    MemberBalanceSkeleton,
    useI18n,
    useMemberContext,
    useOccConfig,
    useOccDisplayText,
    withComponentWrapper,
} from '@vista-digital/occ-ui'
import { memberBalanceConfigValidator } from '@vista-digital/occ-ui/src/lib/components/member/balance/types/config'
import { memberBalanceDisplayTextValidator } from '@vista-digital/occ-ui/src/lib/components/member/balance/types/display-text'
import { defaultMemberBalanceProps } from '@vista-digital/occ-ui/src/lib/components/member/balance/types/props'
import { getMonetaryValueLabelForMemberBalance } from '@vista-digital/occ-ui/src/lib/components/member/balance/utils/get-monetary-value-label-for-member-balance'
import { Validator } from '@vista-digital/validator'
import { observer } from 'mobx-react'
import type { FC } from 'react'
import removeDuplicateWordsFromText from '../utils/removeDuplicateWordsFromText'

/**
 * Component copied from occ-ui, extended to allow passing a specific balance type id.
 */

const b = bemClassBuilder('v-member-balance')

interface MemberBalancePropsWithBalanceTypeId extends MemberBalanceProps {
    balanceTypeId: number
}

// eslint-disable-next-line complexity -- copied from occ-ui
export const MemberBalance: FC<MemberBalancePropsWithBalanceTypeId> = observer(props => {
    const { member: memberDisplayText } = useOccDisplayText()
    const { enableSkeletonLoadingIndicators } = useOccConfig()
    const { displayText, config, balanceTypeId } = props
    const { member } = useMemberContext()
    const i18n = useI18n()

    if (!member) {
        return enableSkeletonLoadingIndicators ? <MemberBalanceSkeleton /> : null
    }

    const balance = getMemberBalances()?.find(memberBalance => memberBalance.balanceTypeId === balanceTypeId)
    if (!balance) {
        logger.error(`member ${member.id} does not have a balance type with id ${balanceTypeId} configured`)
        return null
    }

    const balanceType = getBalanceTypeById(balance.balanceTypeId)
    if (!balanceType) {
        logger.error(`No balance type for balanceTypeId: ${balance.balanceTypeId}`)
        return null
    }

    // TODO: Add support for multiple balances and expiries. #286581
    const expiry = balance.expiries?.length ? balance.expiries[0] : undefined

    const balanceUnits = i18n.template(displayText.availableUnitsFormat, {
        availableUnits: i18n.template(
            memberDisplayText.memberBalanceFormat,
            {
                units: balance.availableUnits,
                unitName: balanceType.unitName ?? memberDisplayText.defaultBalanceTypeUnitName,
            },
            // unitDecimalPlaces exists in data but not on OCC type, maybe be added in v16?
            { number: { decimalPrecision: (balanceType?.['unitDecimalPlaces'] as number) || 0 } }
        ),
    })

    const monetaryValue = getMonetaryValueLabelForMemberBalance(
        i18n,
        displayText.monetaryValueFormat,
        balance.availableUnits,
        balanceType.paymentConversionRate
    )

    const clubName = i18n.translatable(balanceType.name)

    let primaryValue =
        !monetaryValue || config.primaryValue === MemberBalancePrimaryValue.BalanceUnits ? balanceUnits : monetaryValue

    primaryValue = removeDuplicateWordsFromText(primaryValue, clubName)

    const secondaryValue = monetaryValue
        ? config.primaryValue === MemberBalancePrimaryValue.BalanceUnits
            ? monetaryValue
            : balanceUnits
        : undefined

    const showSecondaryValue = config.showSecondaryValue && secondaryValue

    return (
        <div className={b.block()}>
            <div className={b.element('values-container')}>
                <DisplayText
                    className={b.element('primary-value', { [config.primaryValue]: true })}
                    label={displayText.primaryValueFormat}
                    templateParameters={{ primaryValue }}
                />
                {showSecondaryValue && (
                    <DisplayText
                        className={b.element('secondary-value')}
                        label={displayText.secondaryValueFormat}
                        templateParameters={{ secondaryValue }}
                    />
                )}
            </div>
            <DisplayText className={b.element('club-name')} label={balanceType.name} />
            {expiry && (
                <DisplayText
                    className={b.element('expiry-details')}
                    label={displayText.expiringUnitsFormat}
                    templateParameters={{
                        expiringUnits:
                            balance.availableUnits == expiry.units
                                ? ''
                                : removeDuplicateWordsFromText(
                                      getFormattedMemberBalance(i18n, memberDisplayText, expiry.units, balanceType),
                                      clubName
                                  ),
                        expiresAt: expiry.expiresAt,
                    }}
                />
            )}
        </div>
    )
})

const getFormattedMemberBalance = (
    i18n: OccI18N,
    displayText: OccMemberDisplayText,
    units: number,
    balanceType: BalanceType
) =>
    i18n.template(
        displayText.memberBalanceFormat,
        {
            units,
            unitName: balanceType?.unitName || displayText.defaultBalanceTypeUnitName,
        },
        { number: { decimalPrecision: (balanceType?.['unitDecimalPlaces'] as number) || 0 } }
    )

const memberBalancePropsValidator = Validator.object<MemberBalancePropsWithBalanceTypeId>({
    config: memberBalanceConfigValidator,
    displayText: memberBalanceDisplayTextValidator,
    balanceTypeId: Validator.number(),
})

export const MemberBalanceComponent = withComponentWrapper(
    MemberBalance,
    { ...defaultMemberBalanceProps, balanceTypeId: 1 },
    memberBalancePropsValidator
)
